<template>
  <div class="recruitment-candidates-page">
    <QHeader class="bg-white text-primary shadow-soft">
      <QToolbar>
        <QBtn flat round dense icon="icon-f-arrow-left" @click="goBack" />
        <QToolbarTitle class="toolbar-subtitled">
          {{ headerTitle }}
          <p>{{ headerSubtitle }}</p>
        </QToolbarTitle>

        <QBtn flat icon="icon-f-more-vertical">
          <QMenu content-class="recruitment-candidates-tabs-menu">
            <QList>
              <MenuItem icon="icon-f-info" :label="$t('ETAB.PAGES.RECRUITMENT.@GO_TO_DETAIL')" @on-action="openRecruitmentRecap" />
              <MenuItem
                v-if="!isIgnoredRoute && !isLockedRoute"
                icon="icon-f-eye-off"
                :label="$t('ETAB.PAGES.RECRUITMENT.@GO_TO_IGNORED')"
                @on-action="onGoToIgnored"
              />
              <MenuItem
                v-if="shouldDisplayReportProblem"
                icon="icon-f-alert-triangle"
                :label="$filters.whitespacedPunctuation($t('ETAB.PAGES.RECRUITMENT.@REPORT_PROBLEM'))"
                @on-action="onReportProblem"
              />
              <MenuItem v-if="shouldDisplayCancel" icon="icon-f-x" :label="$t('ETAB.PAGES.RECRUITMENT.@CANCEL_DEMAND')" @on-action="onCancelDemand" />
            </QList>
          </QMenu>
        </QBtn>
      </QToolbar>
      <QTabs v-if="shouldDisplayTabs" align="justify">
        <QRouteTab
          no-caps
          :to="appliedRoute"
          :disable="isLockedRoute"
          :label="$tc('ETAB.PAGES.RECRUITMENT.@CANDIDATES_TAB', recruitment.getAppliedCandidatesAmount())"
        />
        <QRouteTab
          no-caps
          :to="contactedRoute"
          :disable="isLockedRoute"
          :label="$tc('ETAB.PAGES.RECRUITMENT.@CHOSEN_TAB_CONTACTED', recruitment.getSelectedCandidatesAmount())"
        />
      </QTabs>
    </QHeader>

    <router-view />

    <ReportCandidateModal v-model:open="reportModalOpened" @refresh="refreshCandidates" />
  </div>
</template>

<script>
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { ContactManager } from "@core/managers/contact.manager";
  import { RecruitmentsService } from "@core/services";
  import { ETAB_ROUTES } from "@etab/config";
  import {
    GETTER_RECRUITMENT_GET_CURRENT,
    ACTION_RECRUITMENT_GET,
    ACTION_DASHBOARD_OPEN_RECRUITMENT_RECAP_MODAL,
    ACTION_RECRUITMENT_CANDIDATES_GET_CONTACTED,
    GETTER_REPORT_MODAL_GET,
  } from "@etab/stores/types";
  import { useRecruitmentStore } from "@etab/stores/recruitment";
  import { useRecruitmentCandidatesStore } from "@etab/stores/recruitment-candidates";
  import { useReportCandidateStore } from "@etab/stores/report-candidate";
  import { useDashboardStore } from "@etab/stores/dashboard";
  import { mapStores } from "pinia";
  import MenuItem from "@core/components/common/MenuItem.functional.vue";
  import ReportCandidateModal from "@etab/components/recruitments/detail/ReportCandidate.modal.vue";

  export default {
    components: {
      MenuItem,
      ReportCandidateModal,
    },
    computed: {
      ...mapStores(useRecruitmentStore, useRecruitmentCandidatesStore, useReportCandidateStore, useDashboardStore),
      demandId() {
        return this.$route.params.demandId;
      },
      demandType() {
        return this.$route.params.demandType;
      },
      reportModalOpened() {
        return this.reportCandidateStore[GETTER_REPORT_MODAL_GET];
      },
      recruitment() {
        return this.recruitmentStore[GETTER_RECRUITMENT_GET_CURRENT];
      },
      isIgnoredRoute() {
        return this.$route.name === ETAB_ROUTES.dashboard.recruitment.permanent.detail.ignored.name;
      },
      isLockedRoute() {
        return this.$route.name === ETAB_ROUTES.dashboard.recruitment.permanent.detail.locked.name;
      },
      appliedRoute() {
        const query = this.$route.query;
        return {
          name: ETAB_ROUTES.dashboard.recruitment.permanent.detail.applied.name,
          params: { demandId: this.demandId, demandType: this.demandType },
          query: { ...query },
        };
      },
      contactedRoute() {
        const query = this.$route.query;
        return {
          name: ETAB_ROUTES.dashboard.recruitment.permanent.detail.contacted.name,
          params: { demandId: this.demandId, demandType: this.demandType },
          query: { ...query },
        };
      },
      shouldDisplayReportProblem() {
        return !this.recruitment.isArchived();
      },
      shouldDisplayCancel() {
        return !this.recruitment.isArchived();
      },
      shouldDisplayTabs() {
        return this.$route.name !== ETAB_ROUTES.dashboard.recruitment.permanent.detail.ignored.name;
      },
      contactedCandidatesAmount() {
        return this.recruitment.getSelectedCandidatesAmount();
      },
      appliedCandidatesAmount() {
        return this.recruitment.getAppliedCandidatesAmount();
      },
      headerTitle() {
        let headerTitle = "";

        if (this.recruitment.isCDI()) {
          headerTitle = this.$t("ETAB.PAGES.RECRUITMENT.@TITLE_CDI");
        }
        if (this.recruitment.isSeason()) {
          headerTitle = this.$t("ETAB.PAGES.RECRUITMENT.@TITLE_SEASON");
        }
        if (this.isIgnoredRoute) {
          headerTitle = this.$t("ETAB.PAGES.RECRUITMENT.@TITLE_IGNORED");
        }
        return headerTitle;
      },
      headerSubtitle() {
        let headerSubtitle = "";

        const qualification = this.recruitment.getQualificationLabel();
        headerSubtitle = this.$t("ETAB.PAGES.RECRUITMENT.@SUBTITLE_QUALIFICATION", { qualification });

        return headerSubtitle;
      },
    },
    created() {
      // if we come directly to the parent route, we redirect to the right child
      if (this.$route.name === ETAB_ROUTES.dashboard.recruitment.permanent.detail.root.name) {
        this.redirect();
      }
    },
    mounted() {
      this.$_stopGlobalLoading();
    },
    methods: {
      redirect() {
        if ((this.recruitment.isExpired() || this.recruitment.hasStarted()) && this.contactedCandidatesAmount > 0) {
          // if the recruitment is expired or started and we contacted someone, we go directly to the contacted tab
          this.redirectTo(this.contactedRoute);
          return;
        }
        if (this.appliedCandidatesAmount === 0 && this.contactedCandidatesAmount > 0) {
          // if there is no candidates but a contacted extra, go to the contacted tab
          this.redirectTo(this.contactedRoute);
          return;
        }
        if (this.$route.name === this.contactedRoute.name && this.contactedCandidatesAmount === 0 && this.appliedCandidatesAmount === 0) {
          // if we are on the contacted tab and there is no contacted extra, we don't move
          return;
        }
        // otherwise applied candidates is the default tab
        this.redirectTo(this.appliedRoute);
      },
      redirectTo(route) {
        if (this.$route.name !== route.name) {
          this.$router.replace(route);
        }
      },
      openRecruitmentRecap() {
        this.dashboardStore[ACTION_DASHBOARD_OPEN_RECRUITMENT_RECAP_MODAL]({ recap: this.recruitment });
      },
      goBack() {
        if (this.isIgnoredRoute) {
          // RG: go back from ignored to contacted candidates
          this.$router.push({ name: ETAB_ROUTES.dashboard.recruitment.permanent.detail.contacted.name }).catch((suppressedError) => {});
        } else {
          this.goToDashboard();
        }
      },
      goToDashboard() {
        if (this.recruitment.hasStarted()) {
          this.$router.push({ name: ETAB_ROUTES.dashboard.tabs.inProgress.name }).catch((suppressedError) => {});
        } else {
          this.$router.push({ name: ETAB_ROUTES.dashboard.tabs.upcoming.name }).catch((suppressedError) => {});
        }
      },
      onGoToIgnored() {
        const query = this.$route.query;
        this.$router
          .push({
            name: ETAB_ROUTES.dashboard.recruitment.permanent.detail.ignored.name,
            params: { demandId: this.demandId },
            query: { ...query },
          })
          .catch((suppressedError) => {});
      },
      onReportProblem() {
        const subject = this.$t("EMAILS.@DEMAND_PROBLEM", { demandId: this.demandId });
        ContactManager.contactEtabSupport({ subject });
      },
      async cancelDemand() {
        try {
          this.$_startGlobalLoading();
          await RecruitmentsService.cancelPermanentDemand({ demandId: this.demandId });
          // return to dashboard, the loading will stop there
          this.goToDashboard();
        } catch (error) {
          // we stop loading in case of problem
          this.$_stopGlobalLoading();
          NotificationsManager.error();
          throw error;
        }
      },
      async onCancelDemand() {
        this.$q
          .dialog({
            title: this.$t("ETAB.PAGES.RECRUITMENT.CANCEL_DIALOG.@TITLE"),
            message: this.$t("ETAB.PAGES.RECRUITMENT.CANCEL_DIALOG.@MESSAGE"),
            ok: {
              label: this.$t("ETAB.PAGES.RECRUITMENT.CANCEL_DIALOG.@CONFIRM"),
              color: "secondary",
              noCaps: true,
            },
            cancel: {
              label: this.$t("ETAB.PAGES.RECRUITMENT.CANCEL_DIALOG.@CLOSE"),
              flat: true,
              noCaps: true,
            },
          })
          .onOk(() => this.cancelDemand());
      },
      async refreshCandidates() {
        try {
          this.$_startGlobalLoading();
          await Promise.all([
            this.recruitmentStore[ACTION_RECRUITMENT_GET]({ demandId: this.demandId, demandType: this.demandType }),
            this.recruitmentCandidatesStore[ACTION_RECRUITMENT_CANDIDATES_GET_CONTACTED]({ demandId: this.demandId, type: this.demandType }),
          ]);
        } catch (error) {
          NotificationsManager.error();
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
    },
  };
</script>
